import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import client from '@/api/client';
import type { Hostel } from '@/api/types';
import { CheckCircle, XCircle, Clock, Building2, User } from 'lucide-react';
import { useState } from 'react';
import { Pagination } from '@/components/ui/Pagination';
import { usePagination, paginateArray } from '@/hooks/usePagination';

interface PendingOwner {
  id: string;
  email: string;
  fullName: string;
  phone?: string;
  companyName?: string;
  isActive: boolean;
  createdAt: string;
}

interface ApprovalsData {
  pendingOwners: PendingOwner[];
  pendingHostels: Hostel[];
  stats: { pendingOwnerCount: number; pendingHostelCount: number };
}

export function AdminApprovalsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const ownersPagination = usePagination();
  const hostelsPagination = usePagination();

  const { data, isLoading } = useQuery<ApprovalsData>({
    queryKey: ['admin-approvals'],
    queryFn: () => client.get('/api/admin/approvals').then(r => r as unknown as ApprovalsData),
    refetchInterval: 30_000,
  });

  const approveOwner = useMutation({
    mutationFn: (id: string) => client.post(`/api/admin/owners/${id}/approve`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-approvals'] }),
  });

  const approveHostel = useMutation({
    mutationFn: (id: string) => client.post(`/api/admin/hostels/${id}/approve`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-approvals'] }),
  });

  const rejectHostel = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      client.post(`/api/admin/hostels/${id}/reject?reason=${encodeURIComponent(reason)}`),
    onSuccess: () => { setRejectId(null); setReason(''); void qc.invalidateQueries({ queryKey: ['admin-approvals'] }); },
  });

  const d = data ?? { pendingOwners: [], pendingHostels: [], stats: { pendingOwnerCount: 0, pendingHostelCount: 0 } };
  const total = d.stats.pendingOwnerCount + d.stats.pendingHostelCount;

  const pagedOwners = paginateArray(d.pendingOwners, ownersPagination.page, ownersPagination.size);
  const pagedHostels = paginateArray(d.pendingHostels, hostelsPagination.page, hostelsPagination.size);

  const fmt = (iso: string) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.pendingApprovalsTitle', 'Pending Approvals')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('admin.pendingApprovalsSubtitle', 'All items requiring your review')}</p>
        </div>
        {total > 0 && (
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm font-medium">
            <Clock size={14} /> {t('admin.itemsPending', '{{count}} item(s) pending', { count: total })}
          </span>
        )}
      </div>

      {isLoading && (
        <div className="bg-white rounded-xl p-10 text-center border border-gray-100">
          <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-400 text-sm">{t('common.loading')}</p>
        </div>
      )}

      {!isLoading && (
      <>
      {/* Pending Owner Accounts */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <User size={16} className="text-blue-600" />
          <h2 className="font-semibold text-gray-800">{t('admin.newOwnerRegistrations', 'New Owner Registrations')}</h2>
          {d.stats.pendingOwnerCount > 0 && (
            <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full">{d.stats.pendingOwnerCount}</span>
          )}
        </div>

        {d.pendingOwners.length === 0 ? (
          <div className="bg-white rounded-xl p-6 text-center border border-dashed border-gray-200 text-gray-400 text-sm">
            <CheckCircle size={24} className="mx-auto mb-2 text-green-300" />
            {t('admin.noPendingOwnerRegistrations', 'No pending owner registrations')}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">{t('admin.owner', 'Owner')}</th>
                  <th className="px-4 py-3 text-left">{t('admin.company', 'Company')}</th>
                  <th className="px-4 py-3 text-left">{t('admin.registered', 'Registered')}</th>
                  <th className="px-4 py-3 text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pagedOwners.map(o => (
                  <tr key={o.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{o.fullName}</p>
                      <p className="text-xs text-gray-500">{o.email}</p>
                      {o.phone && <p className="text-xs text-gray-400">{o.phone}</p>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{o.companyName || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{fmt(o.createdAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => approveOwner.mutate(o.id)}
                        disabled={approveOwner.isPending}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium disabled:opacity-50"
                      >
                        <CheckCircle size={13} /> {t('admin.approve', 'Approve')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              page={ownersPagination.page}
              totalPages={Math.ceil(d.pendingOwners.length / ownersPagination.size) || 0}
              totalElements={d.pendingOwners.length}
              size={ownersPagination.size}
              onPageChange={ownersPagination.setPage}
              className="px-4 pb-3"
            />
          </div>
        )}
      </section>

      {/* Pending Hostel Approvals */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Building2 size={16} className="text-teal-600" />
          <h2 className="font-semibold text-gray-800">{t('admin.hostelRegistrationRequests', 'Hostel Registration Requests')}</h2>
          {d.stats.pendingHostelCount > 0 && (
            <span className="bg-teal-100 text-teal-700 text-xs font-bold px-2 py-0.5 rounded-full">{d.stats.pendingHostelCount}</span>
          )}
        </div>

        {d.pendingHostels.length === 0 ? (
          <div className="bg-white rounded-xl p-6 text-center border border-dashed border-gray-200 text-gray-400 text-sm">
            <CheckCircle size={24} className="mx-auto mb-2 text-green-300" />
            {t('admin.noPendingHostelRequests', 'No pending hostel requests')}
          </div>
        ) : (
          <div>
          <div className="grid gap-3">
            {pagedHostels.map(h => (
              <div key={h.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex items-start justify-between gap-4">
                <div>
                  <p className="font-semibold text-gray-900">{h.name}</p>
                  <p className="text-sm text-gray-500 mt-0.5">{h.address}, {h.city}, {h.state} – {h.pincode}</p>
                  {h.phone && <p className="text-xs text-gray-400 mt-0.5">📞 {h.phone}</p>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => approveHostel.mutate(h.id)}
                    disabled={approveHostel.isPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium disabled:opacity-50"
                  >
                    <CheckCircle size={13} /> {t('admin.approve', 'Approve')}
                  </button>
                  <button
                    onClick={() => { setRejectId(h.id); setReason(''); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium"
                  >
                    <XCircle size={13} /> {t('admin.reject', 'Reject')}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <Pagination
            page={hostelsPagination.page}
            totalPages={Math.ceil(d.pendingHostels.length / hostelsPagination.size) || 0}
            totalElements={d.pendingHostels.length}
            size={hostelsPagination.size}
            onPageChange={hostelsPagination.setPage}
            className="px-1 pb-1"
          />
          </div>
        )}
      </section>
      </>
      )}

      {/* Reject modal */}
      {rejectId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-chrome-page rounded-xl p-6 w-96 shadow-xl">
            <h3 className="font-semibold mb-4">{t('admin.reasonForRejection', 'Reason for Rejection')}</h3>
            <textarea
              rows={3}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder={t('admin.enterRejectionReason', 'Enter rejection reason…')}
              className="w-full border rounded-lg px-3 py-2 text-sm mb-4 resize-none"
            />
            <div className="flex gap-2">
              <button
                onClick={() => rejectHostel.mutate({ id: rejectId, reason })}
                disabled={!reason.trim() || rejectHostel.isPending}
                className="flex-1 bg-red-600 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-40"
              >
                {rejectHostel.isPending ? t('admin.rejecting', 'Rejecting…') : t('admin.reject', 'Reject')}
              </button>
              <button onClick={() => setRejectId(null)} className="flex-1 border rounded-lg py-2 text-sm">{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
